import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, BookmarkIcon, MapPinIcon, AcademicCapIcon,
         BriefcaseIcon, StarIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid, BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid';
import api from '../api/axios';
import toast from 'react-hot-toast';

const PLACEHOLDER = '/assets/no-photo.svg';

export default function ProfileCard({ profile, onShortlistChange }) {
  const [liked,      setLiked]      = useState(profile.interest_status === 'pending' || profile.interest_status === 'accepted');
  const [shortlisted, setShortlisted] = useState(!!profile.is_shortlisted);
  const [loading,    setLoading]    = useState(false);

  const photo = profile.primary_photo || profile.photos?.[0] || PLACEHOLDER;

  const sendInterest = async () => {
    try {
      setLoading(true);
      await api.post(`/interests/${profile.id}`);
      setLiked(true);
      toast.success('Interest sent! 💌');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send interest');
    } finally {
      setLoading(false);
    }
  };

  const toggleShortlist = async () => {
    try {
      if (shortlisted) {
        await api.delete(`/shortlists/${profile.id}`);
        setShortlisted(false);
        toast('Removed from shortlist');
      } else {
        await api.post(`/shortlists/${profile.id}`);
        setShortlisted(true);
        toast.success('Shortlisted! ⭐');
      }
      onShortlistChange?.();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const denominationLabel = {
    catholic: 'Catholic', csi: 'CSI', cni: 'CNI', pentecostal: 'Pentecostal',
    assemblies_of_god: 'Assemblies of God', seventh_day_adventist: 'SDA',
    methodist: 'Methodist', lutheran: 'Lutheran', brethren: 'Brethren',
    baptist: 'Baptist', church_of_god: 'Church of God', other: 'Other'
  };

  return (
    <div className="card group hover:shadow-md transition-shadow duration-200">
      {/* Photo */}
      <Link to={`/profile/${profile.id}`} className="block relative">
        <img
          src={photo}
          alt={`${profile.first_name} ${profile.last_name}`}
          className="w-full h-56 object-cover"
          onError={(e) => { e.target.src = PLACEHOLDER; }}
        />
        {profile.is_premium && (
          <span className="absolute top-2 left-2 badge-premium">
            <StarIcon className="w-3 h-3" /> Premium
          </span>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </Link>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <Link to={`/profile/${profile.id}`}>
              <h3 className="font-heading font-semibold text-gray-900 hover:text-primary transition-colors">
                {profile.first_name} {profile.last_name}
              </h3>
            </Link>
            <p className="text-xs text-gray-500 font-mono">{profile.profile_id}</p>
          </div>
          <span className="text-lg font-bold text-primary">{profile.age} yrs</span>
        </div>

        <div className="space-y-1 mb-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <MapPinIcon className="w-3.5 h-3.5 text-primary-400" />
            {profile.district}, {profile.state || 'AP/TS'}
          </div>
          {profile.denomination && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <img src="/cross-icon.png" alt="Cross" className="w-3.5 h-3.5 rounded object-cover" />
              {denominationLabel[profile.denomination] || profile.denomination}
            </div>
          )}
          {profile.education_level && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <AcademicCapIcon className="w-3.5 h-3.5 text-primary-400" />
              {profile.education_level.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </div>
          )}
          {profile.occupation_type && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <BriefcaseIcon className="w-3.5 h-3.5 text-primary-400" />
              {profile.occupation_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={sendInterest}
            disabled={liked || loading}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
              liked
                ? 'bg-red-50 text-red-500 cursor-default'
                : 'bg-primary text-white hover:bg-primary-700 active:scale-95'
            }`}
          >
            {liked ? <HeartSolid className="w-4 h-4" /> : <HeartIcon className="w-4 h-4" />}
            {liked ? 'Sent' : 'Send Interest'}
          </button>
          <button
            onClick={toggleShortlist}
            className={`p-2 rounded-lg border transition-all active:scale-95 ${
              shortlisted
                ? 'bg-gold text-white border-gold'
                : 'border-gray-200 text-gray-500 hover:border-gold hover:text-gold'
            }`}
          >
            {shortlisted ? <BookmarkSolid className="w-5 h-5" /> : <BookmarkIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
